import axios from 'axios';
import Cookies from 'js-cookie';

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * 获取内网 API 地址
 *
 * Next.js standalone 模式下，serverRuntimeConfig 的值会在构建时被烘焙，
 * 无法在运行时动态读取。因此直接使用 process.env。
 *
 * - 构建时：Dockerfile ENV 设置为外网地址，ISR 预渲染成功
 * - 运行时：CMD 中 source .env.production，process.env 获取内网地址
 */
function getInternalApiHost(): string {
  return process.env.NEXT_APP_INTERNAL_API_HOST || '';
}

const instance = axios.create({
  baseURL: process.env.NEXT_APP_API_HOST,
  timeout: 60000,
  headers: {
    tenantId: process.env.NEXT_APP_TENANTID,
    'time-zone': timeZone,
  },
});

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('userToken');
    if (token != undefined) {
      config.headers['Authorization'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 服务端渲染内网调用专用axios实例
// 注意：不在这里设置 baseURL，因为构建时的值会被烘焙
// 改为在请求拦截器中动态获取，确保运行时能读到正确的环境变量
const serverInstance = axios.create({
  timeout: 30000,
  headers: {
    tenantId: `${process.env.NEXT_APP_TENANTID}`,
  },
});

// 服务端实例响应拦截器
serverInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 服务端错误处理，可以添加日志记录
    console.error('服务端API调用错误:', error);
    return Promise.reject(error);
  }
);

// 服务端实例请求拦截器
// 关键：在每次请求时动态获取 baseURL，而不是模块加载时
serverInstance.interceptors.request.use(
  (config) => {
    // 动态获取内网 API 地址
    // 运行时 process.env 来自 CMD 中 source 的 .env.production
    const internalApiHost = getInternalApiHost();
    config.baseURL = internalApiHost;

    // 打印API endpoint信息
    console.log('服务端API调用:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      headers: config.headers,
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default instance;
export { serverInstance };

export type BizFuncNameType =
  | 'main'
  | 'recent_follow'
  | 'unfollow'
  | 'comment_viewer'
  | 'stories_viewer'
  | 'follow_export'
  | 'post_likes'
  | 'threads'
  | 'hash_tag'
  | 'shadowban'
  | 'email_finder';

export type CostHeaderParamsType = {
  bizFuncName: BizFuncNameType;
  isPremium?: boolean;
};

type CreateCostHeaderType = <T extends BizFuncNameType>(
  options: CostHeaderParamsType
) => Readonly<
  | {
      'is-free': 'false' | 'true';
      'biz-func-name': T;
    }
  | {}
>;

export const createCostHeader: CreateCostHeaderType = function (options: CostHeaderParamsType) {
  if (!options?.bizFuncName || typeof options?.isPremium !== 'boolean') {
    return {};
  }
  return {
    'is-free': options.isPremium ? 'false' : 'true',
    'biz-func-name': options.bizFuncName,
  };
};
